import React, { useEffect, useState } from "react";
import { Box, useMediaQuery, useTheme, Fade } from "@mui/material";
import BackspaceOutlinedIcon from "@mui/icons-material/BackspaceOutlined";
import { keyframes } from "@mui/system";
import { SEATTLE_TIMES } from "../../../../fcg/wordy/WordyGameEmbed";

// Slight bounce animation (copied from GameControls for consistency)
const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  40% { transform: translateY(-5px); }
  60% { transform: translateY(-3px); }
`;

const KEYBOARD_KEYS = [
  ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
  ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
  ["ENTER", "Z", "X", "C", "V", "B", "N", "M", "⌫"],
];

// Cutoff date: April 13, 2025
const TOOLTIP_CUTOFF_DATE = new Date(2025, 3, 13); // Note: months are 0-indexed

const Keyboard = ({
  onGuessUpdate,
  currentGuess,
  wordData,
  gameOver,
  guesses,
  correctColor,
  presentColor,
  absentColor,
  keyboardBackgroundColor,
  publicationId,
}) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));

  // State to control temporary keyboard tooltip visibility
  const [showKeyboardTooltip, setShowKeyboardTooltip] = useState(false);

  // Function to determine if tooltip should be shown
  const shouldShowTooltip = () => {
    // Only show for Seattle Times
    if (publicationId !== SEATTLE_TIMES) {
      return false;
    }

    // Check if within date range
    const now = new Date();
    if (now >= TOOLTIP_CUTOFF_DATE) {
      return false;
    }

    // Check if user has made at least one guess
    if (!guesses || guesses.length === 0) {
      return false;
    }

    // Check if tooltip has already been shown to this user
    const tooltipShown = localStorage.getItem(
      `keyboard_tooltip_shown_${publicationId}`
    );
    if (tooltipShown === "true") {
      return false;
    }

    return true;
  };

  // Effect to determine if the tooltip should be shown
  useEffect(() => {
    if (shouldShowTooltip()) {
      setShowKeyboardTooltip(true);

      // Set a timeout to hide the tooltip after 10 seconds
      const timeoutId = setTimeout(() => {
        setShowKeyboardTooltip(false);
        // Remember that we showed it to this user
        localStorage.setItem(`keyboard_tooltip_shown_${publicationId}`, "true");
      }, 3000);

      return () => clearTimeout(timeoutId);
    }
  }, [guesses, publicationId]);

  // Keyboard event handler
  const handleKeyPress = (e) => {
    if (!wordData || gameOver) return;

    if (e.key === "Enter" && currentGuess.length === wordData.word.length) {
      e.preventDefault(); // Prevent form submission
      onGuessUpdate("ENTER");
    } else if (e.key === "Backspace") {
      e.preventDefault();
      onGuessUpdate("BACKSPACE");
    } else if (
      /^[A-Za-z]$/.test(e.key) &&
      currentGuess.length < wordData.word.length
    ) {
      e.preventDefault();
      onGuessUpdate(e.key.toUpperCase());
    }
  };

  // Add keyboard event listener
  useEffect(() => {
    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, [currentGuess, gameOver, wordData, onGuessUpdate]);

  // On-screen keyboard handler
  const handleKeyClick = (key) => {
    if (gameOver) return;

    if (key === "ENTER" && currentGuess.length === wordData.word.length) {
      onGuessUpdate("ENTER");
    } else if (key === "⌫") {
      onGuessUpdate("BACKSPACE");
    } else if (currentGuess.length < wordData.word.length) {
      onGuessUpdate(key);
    }
  };

  const getKeyboardKeyColor = (key) => {
    if (!wordData || !guesses) return [keyboardBackgroundColor, "black"];

    const keyUpperCase = key.toUpperCase();
    const wordUpperCase = wordData.word.toUpperCase();
    let keyFound = false;
    let correctPosition = false;

    const letterInWord = wordUpperCase.includes(keyUpperCase);

    if (letterInWord) {
      for (let guess of guesses) {
        const guessArray = guess.toUpperCase().split("");
        for (let i = 0; i < guessArray.length; i++) {
          if (guessArray[i] === keyUpperCase) {
            keyFound = true;
            if (keyUpperCase === wordUpperCase[i]) {
              correctPosition = true;
              break;
            }
          }
        }
        if (correctPosition) break;
      }
    }

    if (correctPosition) return correctColor;
    if (letterInWord && keyFound) return presentColor;
    return guesses.some((guess) => guess.toUpperCase().includes(keyUpperCase))
      ? absentColor
      : [keyboardBackgroundColor, "black"];
  };

  // Calculate responsive key sizes based on available width
  const getKeySize = (key, rowLength) => {
    // Calculate percentage of row width that each key should occupy
    // Special keys like ENTER and backspace get more space
    const baseKeyWidth = key === "ENTER" || key === "⌫" ? 1.5 : 1;

    // We use percentages to ensure keys take up full width
    return {
      width: `${
        (baseKeyWidth /
          (rowLength + (key === "ENTER" || key === "⌫" ? 0.5 : 0))) *
        100
      }%`,
      height: { xs: "48px", sm: "52px", md: "58px" },
      fontSize: {
        xs: key === "ENTER" ? "12px" : "14px",
        sm: key === "ENTER" ? "13px" : "15px",
        md: key === "ENTER" ? "14px" : "16px",
      },
      // Maintain minimum width for touch targets
      minWidth: {
        xs: key === "ENTER" || key === "⌫" ? "40px" : "24px",
        sm: key === "ENTER" || key === "⌫" ? "45px" : "30px",
      },
    };
  };

  return (
    <Box
      sx={{
        mt: { xs: 1, sm: 2 },
        width: "100%",
        maxWidth: "sm",
        mx: "auto",
        // overflow: "hidden",
        px: { xs: 0.5, sm: 1 },
        position: "relative", // Added to position tooltip
      }}
    >
      {/* Temporary Keyboard Tooltip - will be removed after April 13, 2025 */}
      {showKeyboardTooltip && (
        <Fade in={showKeyboardTooltip}>
          <Box
            sx={{
              position: "absolute",
              top: -40,
              left: 0,
              transform: "translateX(0)",
              backgroundColor: "#FFF7E9",
              color: "#333",
              px: 1.5,
              py: 0.5,
              borderRadius: 10,
              fontSize: "0.75rem",
              fontWeight: "500",
              whiteSpace: "nowrap",
              boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
              border: "1px solid rgba(0,0,0,0.1)",
              zIndex: 5,
              animation: `${bounce} 1.5s ease infinite`,
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: -8,
                left: "15%",
                marginLeft: -4,
                borderWidth: 4,
                borderStyle: "solid",
                borderColor: "#FFF7E9 transparent transparent transparent",
              },
            }}
          >
            Psst... Used letters now turn grey!
          </Box>
        </Fade>
      )}

      {KEYBOARD_KEYS.map((row, i) => (
        <Box
          key={i}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            gap: { xs: "2px", sm: "4px" },
            mb: { xs: "4px", sm: "6px" },
          }}
        >
          {row.map((key) => {
            const keySize = getKeySize(key, row.length);
            return (
              <Box
                key={key}
                onClick={() => handleKeyClick(key)}
                sx={{
                  backgroundColor: getKeyboardKeyColor(key)[0],
                  color: getKeyboardKeyColor(key)[1],
                  ...keySize,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "4px",
                  fontWeight: "bold",
                  border: "0.5px solid black",
                  cursor: "pointer",
                  userSelect: "none",
                  textTransform: "uppercase",
                  transition: "transform 0.1s",
                  "&:hover": {
                    transform: "scale(1.05)",
                  },
                  px: { xs: 0.25, sm: 0.5 },
                }}
              >
                {key === "⌫" ? <BackspaceOutlinedIcon fontSize="small" /> : key}
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

export default Keyboard;
